import { useState, useEffect, useCallback } from 'react';
import { ObjectiveQuestion } from '../../../types';
import { ObjectiveQuestionState } from '../utils/types';
import { findCorrectAnswer, findUserMarkedAnswer } from '../utils/utils';

interface UseObjectiveQuestionStateProps {
  question: ObjectiveQuestion;
  isTest: boolean;
  resolved?: boolean;
  selectedAnswer?: number;
  setSelectedAnswer?: (answer: number) => void;
  handleSubmit?: () => void;
}

export const useObjectiveQuestionState = ({
  question,
  isTest,
  resolved,
  selectedAnswer,
  setSelectedAnswer,
  handleSubmit,
}: UseObjectiveQuestionStateProps) => {
  const [state, setState] = useState<ObjectiveQuestionState>({
    questionAnswer: selectedAnswer,
    questionAnswerCorrect: undefined,
    questionVideoStart: false,
    questionTextShow: false,
    modalVideoOption: false,
    LSBVideo: false,
    formulaModalShow: false,
  });

  useEffect(() => {
    if (resolved) return;

    setState((prev) => ({
      ...prev,
      questionAnswer: isTest ? selectedAnswer : undefined,
      questionAnswerCorrect: undefined,
      questionVideoStart: false,
      questionTextShow: false,
      modalVideoOption: false,
      LSBVideo: false,
      formulaModalShow: false,
    }));
  }, [question.id_questao, isTest, selectedAnswer, resolved]);

  useEffect(() => {
    if (resolved && question.answers) {
      const correctAnswer = findCorrectAnswer(question.answers);
      const userMarkedAnswer = findUserMarkedAnswer(question.answers);

      let userAnswer: number | undefined;
      if (selectedAnswer !== undefined) {
        userAnswer = selectedAnswer;
      } else if (userMarkedAnswer !== undefined) {
        userAnswer = userMarkedAnswer;
      }

      setState((prev) => ({
        ...prev,
        questionAnswerCorrect: correctAnswer,
        questionAnswer: userAnswer,
        questionVideoStart: false,
        questionTextShow: false,
        modalVideoOption: false,
        LSBVideo: false,
        formulaModalShow: false,
      }));
    }
  }, [resolved, question.answers, question.id_questao, selectedAnswer]);

  const handlePlayVideo = useCallback(() => {
    if (question?.video_embed_libras) {
      setState((prev) => ({ ...prev, LSBVideo: true, questionVideoStart: true }));
    } else {
      setState((prev) => ({ ...prev, questionVideoStart: true }));
    }
    //eslint-disable-next-line
  }, [question?.video_embed_libras]);

  const handleSubmitCheck = useCallback(() => {
    if (handleSubmit) {
      handleSubmit();
      return;
    }

    const correctAnswer = findCorrectAnswer(question.answers);
    setState((prev) => ({ ...prev, questionAnswerCorrect: correctAnswer }));
  }, [question.answers, handleSubmit]);

  const selectAnswer = useCallback(
    (answer: number) => {
      setState((prev) => ({
        ...prev,
        questionAnswerCorrect: undefined,
        questionAnswer: answer,
      }));
      setSelectedAnswer?.(answer);
    },
    [setSelectedAnswer]
  );

  const setQuestionTextShow = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, questionTextShow: show }));
  }, []);

  const setQuestionVideoStart = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, questionVideoStart: show }));
  }, []);

  const setModalVideoOption = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, modalVideoOption: show }));
  }, []);

  const setLSBVideo = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, LSBVideo: show }));
  }, []);

  const setFormulaModalShow = useCallback((show: boolean) => {
    setState((prev) => ({ ...prev, formulaModalShow: show }));
  }, []);

  return {
    state,
    handlePlayVideo,
    handleSubmitCheck,
    selectAnswer,
    setQuestionTextShow,
    setQuestionVideoStart,
    setModalVideoOption,
    setLSBVideo,
    setFormulaModalShow,
  };
};
